import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AuraMark } from "@/components/AuraMark";
import { MuseLink } from "@/components/MuseLink";
import { DnaExtras } from "@/components/DnaExtras";
import { MatchedSounds } from "@/components/MatchedSounds";
import { startArrangement, stopArrangement, updateMuteFn, setMasterVolume, keyRoot } from "@/lib/arrangementPlayer";
import {
  FolderKanban, PenLine, Boxes, Grid3x3, MonitorSpeaker, Receipt, Pencil, ClipboardList, FileText, Mic,
  Sparkles, Dna as DnaIcon, Clock, ListMusic, AudioLines, ChevronRight, Volume2, VolumeX, SlidersHorizontal,
  Settings, CircleDot, Play, Pause, Square, Loader2, Music2, CheckCircle2, Plus, FileMusic, Save,
  Download, Disc3, Send, ChevronDown,
} from "lucide-react";

const TRACKS = [
  { name: "Drums", icon: Music2, type: "wave", color: "#9aa0ab" },
  { name: "808", icon: AudioLines, type: "clip", color: "#C77B3A", sub: "MIDI" },
  { name: "Percussion", icon: ListMusic, type: "clip", color: "#8A924E" },
  { name: "Bass", icon: AudioLines, type: "clip", color: "#5B7BA6" },
  { name: "Keys", icon: Music2, type: "clip", color: "#8A6FB0" },
  { name: "Vocals", icon: Mic, type: "wave", color: "#9aa0ab" },
  { name: "FX / Atmos", icon: Sparkles, type: "fx", color: "#3E8E8E" },
];
const DEFAULT_SECTIONS = [
  { name: "Intro", tone: "dark" }, { name: "Hook 1", tone: "blue" }, { name: "Verse 1", tone: "slate" },
  { name: "Hook 2", tone: "slate" }, { name: "Verse 2", tone: "teal" }, { name: "Hook 3", tone: "gold" },
  { name: "Outro", tone: "dark" },
];
const toneBg = {
  dark: "#191920", blue: "#28324a", slate: "#232734", teal: "#1f3b37", gold: "#5a3c1c",
};
const toneText = { dark: "#8a8f9c", blue: "#9db4e0", slate: "#aab2c4", teal: "#7fc9bd", gold: "#e0b877" };

function sectionTone(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("hook") || n.includes("chorus")) return n.match(/3|iii/) ? "gold" : "blue";
  if (n.includes("verse")) return n.includes("2") ? "teal" : "slate";
  if (n.includes("bridge") || n.includes("pre")) return "teal";
  return "dark";
}

const SideItem = ({ icon: Icon, label, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-[15px] transition-colors ${
      active ? "bg-white/[0.06] text-gold" : "text-slate-400 hover:text-slate-200"}`}>
    <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.2 : 1.8} /> {label}
  </button>
);

const Clip = ({ color, span }) => (
  <div className="h-full rounded-md relative overflow-hidden" style={{ background: `${color}2e`, border: `1px solid ${color}66`, flex: span }}>
    <div className="absolute inset-0 flex items-center gap-[3px] px-1.5 opacity-70" aria-hidden>
      {Array.from({ length: 22 }).map((_, i) => (
        <span key={i} className="rounded-sm" style={{ width: 2, height: `${20 + ((i * 7) % 60)}%`, background: color }} />
      ))}
    </div>
  </div>
);
const Wave = ({ color }) => (
  <div className="h-full w-full flex items-center gap-[2px] px-1" aria-hidden>
    {Array.from({ length: 90 }).map((_, i) => {
      const h = 12 + Math.abs(Math.sin(i * 0.7) * 70) + ((i * 13) % 20);
      return <span key={i} style={{ width: 2, height: `${Math.min(96, h)}%`, background: color, opacity: 0.55, borderRadius: 2 }} />;
    })}
  </div>
);

export default function Studio() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user, isPrivileged } = useAuth();
  const [project, setProject] = useState(null);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [balance, setBalance] = useState(null);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("text");
  const [busy, setBusy] = useState(false);
  const [dnaLoading, setDnaLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [events, setEvents] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [exporting, setExporting] = useState(null);
  const [approach, setApproach] = useState("");
  const [convo, setConvo] = useState([]);
  const [vocalMap, setVocalMap] = useState(null);
  const [adjustText, setAdjustText] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [mutes, setMutes] = useState(() => new Set());
  const [solos, setSolos] = useState(() => new Set());
  const [masterMute, setMasterMute] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [playPos, setPlayPos] = useState(null);
  const mediaRef = useRef(null); const chunksRef = useRef([]); const pollRef = useRef(null); const recTimer = useRef(null);
  const fileRef = useRef(null);
  const adjustRef = useRef(null); const deliverRef = useRef(null); const phRef = useRef(null);

  const toggleMute = (name) => setMutes((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleSolo = (name) => setSolos((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const laneMuted = (name) => masterMute || mutes.has(name) || (solos.size > 0 && !solos.has(name));
  const focusAdjust = () => { adjustRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => adjustRef.current?.focus(), 300); };
  const scrollToDeliver = () => { (deliverRef.current || adjustRef.current)?.scrollIntoView({ behavior: "smooth", block: "center" }); };

  const togglePlay = async () => {
    if (playing) { stopArrangement(); setPlaying(false); setPlayPos(null); return; }
    const root = keyRoot(project?.dna?.key || project?.plan?.key || project?.dna?.key_signature);
    const tempo = project?.dna?.tempo_bpm || project?.plan?.tempo_bpm || 100;
    const res = await startArrangement({ bpm: tempo, root, isMuted: laneMuted, onPos: setPlayPos });
    if (!res.ok && res.reason === "unsupported") { toast.error("Audio preview isn't supported on this browser"); return; }
    if (!res.ok && res.reason === "muted") toast.info("If you hear nothing, take your device off silent");
    setPlaying(true);
  };

  useEffect(() => { updateMuteFn(laneMuted); });
  useEffect(() => { setMasterVolume(masterMute ? 0 : 0.85); }, [masterMute]);
  useEffect(() => () => { stopArrangement(); }, []);

  const loadProfile = useCallback(async () => { const r = await api.get("/me/profile"); setBalance(r.data.balance); }, []);
  const loadSession = useCallback(async () => {
    try { const r = await api.get(`/projects/${pid}/session`); setApproach(r.data.approach || ""); setConvo(r.data.conversation || []); setVocalMap(r.data.vocal_map || null); }
    catch (e) {}
  }, [pid]);
  const load = useCallback(async () => {
    const [p, s] = await Promise.all([api.get(`/projects/${pid}`), api.get(`/projects/${pid}/creative-inputs`)]);
    setProject(p.data); setSources(s.data);
  }, [pid]);

  useEffect(() => {
    load(); loadProfile(); loadSession();
    api.get("/devices").then((r) => setDevices(r.data.filter((d) => d.status === "connected"))).catch(() => {});
    // hydrate latest job for this project
    api.get("/jobs").then(async (r) => {
      const j = (r.data || []).find((x) => x.project_id === pid);
      if (j) { setJob(j); const ev = await api.get(`/jobs/${j.id}/events`); setEvents(ev.data.events); if (["queued","running"].includes(j.status)) pollJob(j.id); }
    }).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); if (recTimer.current) clearInterval(recTimer.current); };
  }, [load, loadProfile, pid]);

  const handleErr = (e) => {
    const d = e?.response?.data?.detail;
    if (d === "insufficient_credits") toast.error("Not enough credits, top up in Billing.");
    else if (d === "no_entitlement") toast.error("Subscribe or buy credits to run AI.");
    else toast.error(typeof d === "string" ? d : "Something went wrong");
  };

  const addSource = async (kindOverride, content) => {
    const body = { kind: kindOverride || kind, content: content ?? text };
    if (!body.content?.trim()) return;
    setBusy(true);
    try { await api.post(`/projects/${pid}/creative-inputs/text`, body); setText(""); await load(); await loadProfile(); toast.success("Added to session"); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => addSource("text", String(reader.result).slice(0, 2000));
    reader.readAsText(f);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = []; setRecSecs(0);
      recTimer.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        clearInterval(recTimer.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData(); fd.append("file", blob, "voice.webm"); setBusy(true);
        try { await api.post(`/projects/${pid}/creative-inputs/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); await loadProfile(); toast.success("Voice transcribed"); }
        catch (e) { handleErr(e); } finally { setBusy(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch (e) { toast.error("Microphone access denied"); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const genDna = async () => {
    if (sources.length === 0) { toast.error("Add a brief first"); return; }
    setDnaLoading(true);
    try { const r = await api.post(`/projects/${pid}/dna`); setProject((p) => ({ ...p, dna: r.data })); await loadProfile(); toast.success("Production DNA ready"); }
    catch (e) { handleErr(e); } finally { setDnaLoading(false); }
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const r = await api.get(`/jobs/${jobId}/events`); setEvents(r.data.events); setJob(r.data.job);
      loadSession();
      if (["completed", "failed", "canceled"].includes(r.data.job.status)) { clearInterval(pollRef.current); loadProfile(); load(); loadSession(); }
    }, 1200);
  };
  const build = async () => {
    if (!project?.dna) { toast.error("Review Production DNA first"); return; }
    try { const r = await api.post(`/projects/${pid}/jobs/build`); setJob(r.data); setEvents([]); pollJob(r.data.id); toast.success("Building your session"); }
    catch (e) { handleErr(e); }
  };
  const stop = async () => { if (job) { await api.post(`/jobs/${job.id}/cancel`); toast.info("Emergency stop sent"); } };

  const blobDownload = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = href; a.download = filename; a.click(); URL.revokeObjectURL(href);
    } catch (e) { toast.error("Download failed"); }
  };
  const exportBounce = async (type) => {
    setExporting(type);
    try { const r = await api.post(`/projects/${pid}/export`, { type }); await loadProfile(); await blobDownload(`/artifacts/${r.data.artifact_id}/download-file`, r.data.filename); toast.success(type === "stem" ? "Stems rendered" : "Master rendered"); }
    catch (e) { handleErr(e); } finally { setExporting(null); }
  };
  const pushToDevice = async (deviceId) => {
    try { const r = await api.post(`/jobs/${job.id}/push`, { device_id: deviceId }); toast.success(`Sent to ${r.data.device} · ${r.data.daw.toUpperCase()}`); }
    catch (e) { handleErr(e); }
  };

  const sendAdjust = async () => {
    const msg = adjustText.trim();
    if (!msg) return;
    setAdjusting(true);
    setConvo((c) => [...c, { id: `tmp_${Date.now()}`, speaker: "user", message: msg, kind: "adjust" }]);
    setAdjustText("");
    try {
      const r = await api.post(`/projects/${pid}/session/adjust`, { instruction: msg });
      setConvo(r.data.conversation || []);
      await load(); await loadProfile();
      toast.success("Engine adjusted the session");
    } catch (e) { handleErr(e); loadSession(); } finally { setAdjusting(false); }
  };

  const dna = project?.dna; const plan = project?.plan;
  const running = job && ["queued", "running"].includes(job.status);
  const done = job && job.status === "completed";
  const bpm = dna?.tempo_bpm || plan?.tempo_bpm || 100;
  const totalBars = (plan?.sections || []).reduce((s, x) => s + (x.bars || 0), 0) || 75;
  const daw = (project?.target_daw || "reaper");
  const reaperDev = devices.find((d) => d.daw === daw);
  const sections = (plan?.sections?.length ? plan.sections.map((s) => ({ name: s.name, tone: sectionTone(s.name), bars: s.bars })) : DEFAULT_SECTIONS);
  const credits = isPrivileged ? "Unlimited" : (balance?.available ?? 0);
  const initials = (user?.name || user?.email || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  const chars = kind === "text" ? text.length : text.length;

  const showPlayhead = (playing && playPos != null) || running || done;
  const phPct = (playing && Number.isFinite(playPos)) ? playPos * 100
    : running ? Math.min(92, events.length * 12) : done ? 78 : 0;
  useEffect(() => {
    if (phRef.current) phRef.current.style.left = `calc(132px + ${phPct.toFixed(2)} * (100% - 132px) / 100)`;
  }, [phPct, showPlayhead]);

  const feedIcon = (msg = "") => {
    if (/track|created/i.test(msg)) return { I: Plus, c: "#6fae6f" };
    if (/midi|drum|bass|harmon|melody|arrange/i.test(msg)) return { I: FileMusic, c: "#8A6FB0" };
    if (/package|saved|version|complete|deliver/i.test(msg)) return { I: Save, c: "#5B7BA6" };
    return { I: CircleDot, c: "#9aa0ab" };
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--daw-bg)" }}>
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-56 shrink-0 border-r flex-col" style={{ borderColor: "var(--daw-border)", background: "#0C0C0F" }}>
        <button onClick={() => nav("/dashboard")} className="flex items-center gap-2.5 px-5 h-[68px]" data-testid="side-logo">
          <AuraMark className="w-7 h-7" />
          <span className="font-display font-medium text-[18px] tracking-tight text-slate-100">Aura<span className="text-slate-100">Session</span></span>
        </button>
        <nav className="px-3 py-4 space-y-1">
          <SideItem icon={FolderKanban} label="Projects" testid="side-projects" onClick={() => nav("/dashboard")} />
          <SideItem icon={PenLine} label="Create" active testid="side-create" onClick={() => {}} />
          <SideItem icon={Grid3x3} label="Clip Lab" testid="side-clip-lab" onClick={() => nav(`/projects/${pid}/clip-lab`)} />
          <SideItem icon={Boxes} label="Assets" testid="side-assets" onClick={() => toast.info("Assets library, coming soon")} />
          <SideItem icon={MonitorSpeaker} label="Devices" testid="side-devices" onClick={() => nav("/profile")} />
          <SideItem icon={Receipt} label="Billing" testid="side-billing" onClick={() => nav("/profile")} />
        </nav>
        <div className="mt-auto px-5 pb-6">
          <p className="font-mono-a text-[10px] leading-relaxed tracking-[0.18em] text-slate-600 uppercase">Better ideas,<br />realer sessions,<br />AuraSession.</p>
          <div className="mt-4 h-px w-8" style={{ background: "var(--daw-border)" }} />
          <p className="mt-3 text-[10px] text-slate-600">Powered by <span className="text-gold">SoundForgeArena</span></p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <div className="h-[68px] shrink-0 flex items-center gap-3 px-4 sm:px-6 border-b" style={{ borderColor: "var(--daw-border)" }}>
          <button onClick={() => nav("/dashboard")} className="flex lg:hidden items-center gap-2 shrink-0" data-testid="mobile-logo">
            <AuraMark className="w-6 h-6" /><span className="font-display font-medium text-slate-100 text-[15px]">AuraSession</span>
          </button>
          <div data-testid="credits-pill" className="flex items-center gap-2 px-3 py-1.5 rounded-full border ml-auto" style={{ borderColor: "#3a3320", background: "#171308" }}>
            <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] text-black font-bold bg-gold-grad">i</span>
            <span className="text-[13px] text-gold whitespace-nowrap">{typeof credits === "number" ? credits.toLocaleString() : credits}<span className="hidden sm:inline"> credits remaining</span><span className="sm:hidden"> cr</span></span>
          </div>
          <button onClick={() => nav("/profile")} className="flex items-center gap-2.5 shrink-0" data-testid="topbar-user">
            <span className="w-9 h-9 rounded-full bg-gold-grad flex items-center justify-center text-black font-bold text-sm">{initials}</span>
            <span className="text-slate-200 text-sm hidden sm:flex items-center gap-1">{(user?.name || "You").split(" ")[0]} {user?.name?.split(" ")[1]?.[0] ? user.name.split(" ")[1][0] + "." : ""} <ChevronDown className="w-4 h-4 text-slate-500" /></span>
          </button>
        </div>

        {/* Mobile nav strip */}
        <div className="lg:hidden flex items-center gap-2 px-4 py-2 border-b overflow-x-auto" style={{ borderColor: "var(--daw-border)", background: "#0C0C0F" }} data-testid="mobile-nav">
          {[["Projects", () => nav("/dashboard")], ["Create", null], ["Clip Lab", () => nav(`/projects/${pid}/clip-lab`)], ["Devices", () => nav("/profile")], ["Billing", () => nav("/profile")]].map(([lbl, fn]) => (
            <button key={lbl} onClick={fn || (() => {})} className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap ${!fn ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400"}`}>{lbl}</button>
          ))}
        </div>

        <div className="flex-1 grid grid-cols-1 xl:grid-cols-[minmax(0,460px)_1fr] gap-6 p-4 sm:p-6 overflow-y-auto">
          {/* LEFT, Input Studio */}
          <div className="space-y-5">
            <div>
              <p className="font-mono-a text-[11px] tracking-[0.28em] text-slate-500 uppercase">Creative Direction</p>
              <h1 className="font-serif-italic text-3xl sm:text-4xl xl:text-[44px] leading-none text-slate-100 mt-2">Input Studio</h1>
              <p className="text-slate-400 text-sm mt-3 max-w-md">Tell us what you're creating. AuraSession will extract the musical dna and get your session ready.</p>
            </div>

            <div className="rounded-2xl border p-5" style={{ background: "var(--daw-panel)", borderColor: "var(--daw-border)" }}>
              <div className="flex items-center gap-6 border-b pb-3 mb-4" style={{ borderColor: "var(--daw-border)" }}>
                {[["text", PenLine, "Write"], ["muse", ClipboardList, "Paste Muse"], ["txt", FileText, "Upload TXT"], ["voice", Mic, "Speak"]].map(([k, Ic, lbl]) => (
                  <button key={k} data-testid={`tab-${k}`} onClick={() => { setKind(k); if (k === "txt") fileRef.current?.click(); }}
                    className={`flex items-center gap-1.5 text-sm pb-1 -mb-[13px] border-b-2 transition-colors ${kind === k ? "text-gold border-gold" : "text-slate-400 border-transparent hover:text-slate-200"}`}>
                    <Ic className="w-4 h-4" /> {lbl}
                  </button>
                ))}
                <input ref={fileRef} type="file" accept=".txt" hidden onChange={onFile} data-testid="txt-file-input" />
              </div>

              {kind === "voice" ? (
                <div className="flex flex-col items-center gap-4 py-3">
                  {recording ? (
                    <>
                      <div className="flex items-center gap-[3px] h-9" aria-hidden>
                        {Array.from({ length: 40 }).map((_, i) => <span key={i} className="eq-bar w-1 rounded-full" style={{ background: "var(--gold)", height: "40%", animationDelay: `${i * 0.04}s` }} />)}
                      </div>
                      <button data-testid="stop-record-btn" onClick={stopRec} className="text-sm text-red-400">Stop & transcribe · {String(Math.floor(recSecs/60)).padStart(2,"0")}:{String(recSecs%60).padStart(2,"0")}</button>
                    </>
                  ) : (
                    <button data-testid="start-record-btn" onClick={startRec} className="flex flex-col items-center gap-2 text-slate-300">
                      <span className="w-12 h-12 rounded-full border flex items-center justify-center" style={{ borderColor: "var(--gold)" }}><Mic className="w-5 h-5 text-gold" /></span>
                      <span className="text-sm">Record a spoken brief</span>
                    </button>
                  )}
                </div>
              ) : kind === "txt" ? (
                <div className="py-6 text-center text-slate-400 text-sm">Choose a .txt file to import your brief.</div>
              ) : (
                <div className="relative">
                  <textarea data-testid="input-textarea" value={text} maxLength={2000} onChange={(e) => setText(e.target.value)}
                    placeholder={kind === "muse" ? "Paste your Muse Beats export…" : "Looking for a moody, sensual track with a modern global sound…"}
                    rows={7} className="w-full bg-transparent text-slate-200 text-[15px] leading-relaxed resize-none outline-none placeholder:text-slate-600" />
                  <div className="flex items-center justify-between mt-2">
                    <button data-testid="add-input-btn" onClick={() => addSource()} disabled={busy || !text.trim()} className="text-xs text-gold hover:text-gold-soft disabled:opacity-40 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add to session</button>
                    <span className="font-mono-a text-xs text-slate-600">{chars}/2000</span>
                  </div>
                  {kind === "muse" && <p className="mt-2 text-xs text-slate-500">Exported from <MuseLink>Muse</MuseLink>? Paste it above and we'll parse the structure.</p>}
                </div>
              )}
            </div>

            {/* Muse Extraction */}
            <div className="rounded-2xl border p-5" style={{ background: "var(--daw-panel)", borderColor: "var(--daw-border)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Sparkles className="w-4 h-4 text-gold" />
                  <div><p className="text-slate-100 font-medium"><MuseLink>Muse</MuseLink> Extraction</p><p className="text-slate-500 text-xs">We analyzed your brief and extracted the musical DNA.</p></div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500 rotate-180" />
              </div>
              <div className="mt-5 space-y-3.5">
                {[[DnaIcon, "Genre", dna ? `${dna.genre}${dna.sub_genre ? " × " + dna.sub_genre : ""}` : ", "],
                  [Clock, "Tempo", `${bpm} BPM`],
                  [ListMusic, "Structure", plan ? `${totalBars} bars · ${(dna?.arrangement_hint || "").slice(0,18) || "3:00"}` : (dna ? `${dna.time_signature || "4/4"} · ${totalBars} bars` : ", ")],
                  [AudioLines, "Vocal Space", dna?.vocal_space ? "mapped" : ", "]].map(([Ic, k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="flex items-center gap-2.5 text-slate-400 text-sm"><Ic className="w-4 h-4 text-slate-500" /> {k}</span>
                    <span className="text-slate-200 text-sm text-right max-w-[60%] truncate">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Voice input ready */}
            <div className="rounded-2xl border p-5" style={{ background: "var(--daw-panel)", borderColor: "var(--daw-border)" }}>
              <div className="flex items-center gap-2.5 mb-3"><Mic className="w-4 h-4 text-gold" /><div><p className="text-slate-100 font-medium">Voice input ready</p><p className="text-slate-500 text-xs">Speak your idea, hum a vibe, or record a reference.</p></div></div>
              <div className="flex items-center gap-3">
                <button data-testid="voice-quick-record" onClick={recording ? stopRec : startRec} className="w-10 h-10 rounded-full border-2 flex items-center justify-center shrink-0" style={{ borderColor: "var(--gold)" }}>
                  <span className="w-3.5 h-3.5 rounded-full" style={{ background: recording ? "#ef4444" : "var(--gold)" }} />
                </button>
                <div className="flex-1 flex items-center gap-[2px] h-8 overflow-hidden" aria-hidden>
                  {Array.from({ length: 60 }).map((_, i) => <span key={i} className={recording ? "eq-bar" : ""} style={{ width: 2, height: `${15 + Math.abs(Math.sin(i * 0.5)) * 70}%`, background: "var(--gold)", opacity: 0.5, borderRadius: 2, animationDelay: `${i*0.03}s` }} />)}
                </div>
                <span className="font-mono-a text-xs text-slate-400">{String(Math.floor(recSecs/60)).padStart(2,"0")}:{String(recSecs%60).padStart(2,"0")}</span>
              </div>
            </div>

            <button data-testid="review-dna-btn" onClick={genDna} disabled={dnaLoading}
              className="w-full h-12 rounded-xl btn-gold font-semibold flex items-center justify-center gap-2">
              {dnaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Review Production DNA <ChevronRight className="w-4 h-4" /></>}
            </button>

            <DnaExtras pid={pid} dna={dna} vocalMap={vocalMap} initialLocks={project?.dna_locks || {}}
              onDnaLocks={(l) => setProject((p) => ({ ...p, dna_locks: l }))}
              onBriefAdded={async () => { await load(); await loadProfile(); }} />
          </div>

          {/* RIGHT, Session / Aura Vision */}
          <div className="rounded-2xl border flex flex-col min-h-[560px]" style={{ background: "var(--daw-panel)", borderColor: "var(--daw-border)" }}>
            {/* session header */}
            <div className="flex items-center justify-between px-5 h-16 border-b" style={{ borderColor: "var(--daw-border)" }}>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#12241f" }}><AuraMark className="w-5 h-5" /></span>
                <div>
                  <p className="text-slate-100 font-medium text-[15px]">{daw.toUpperCase()} · <span className={reaperDev ? "text-emerald-400" : "text-gold"}>{reaperDev ? "Connected" : "Package mode"}</span> {reaperDev && <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 ml-0.5" />}</p>
                  <p className="text-slate-500 text-xs">{project?.name} · {bpm} BPM · 4/4 · {totalBars} bars</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-slate-500">
                <button data-testid="preview-play-btn" onClick={togglePlay} title={playing ? "Stop preview" : "Play preview"}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${playing ? "text-gold bg-gold/10" : "hover:text-slate-200 hover:bg-white/5"}`}>
                  {playing ? <Square className="w-[17px] h-[17px]" /> : <Play className="w-[18px] h-[18px]" />}
                </button>
                <button data-testid="master-mute-btn" onClick={() => setMasterMute((m) => !m)} title={masterMute ? "Unmute all lanes" : "Mute all lanes"}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${masterMute ? "text-gold bg-gold/10" : "hover:text-slate-200 hover:bg-white/5"}`}>
                  {masterMute ? <VolumeX className="w-[18px] h-[18px]" /> : <Volume2 className="w-[18px] h-[18px]" />}
                </button>
                <button data-testid="header-adjust-btn" onClick={focusAdjust} title="Adjust the session"
                  className="w-9 h-9 rounded-lg flex items-center justify-center hover:text-slate-200 hover:bg-white/5 transition-colors">
                  <SlidersHorizontal className="w-[18px] h-[18px]" />
                </button>
                <button data-testid="header-settings-btn" onClick={() => nav("/profile")} title="Devices & settings"
                  className="w-9 h-9 rounded-lg flex items-center justify-center hover:text-slate-200 hover:bg-white/5 transition-colors">
                  <Settings className="w-[18px] h-[18px]" />
                </button>
              </div>
            </div>

            {/* timeline + tracks */}
            <div className="p-5 flex-1 flex flex-col min-w-0">
              <div className="flex gap-3 min-w-0">
                <div className="w-[132px] shrink-0 hidden sm:block" />
                <div className="flex-1 relative min-w-0 overflow-x-auto">
                  <div className="flex justify-between font-mono-a text-[10px] text-slate-600 mb-1.5">
                    {[1,9,17,25,33,41,49,57,65,73].map((b) => <span key={b}>{b}</span>)}
                  </div>
                  <div className="flex gap-1 min-w-[420px]">
                    {sections.map((s, i) => {
                      const on = activeSection === i;
                      return (
                        <button key={i} data-testid={`section-chip-${i}`}
                          onClick={() => { setActiveSection(on ? null : i); toast.info(`${s.name}${s.bars ? ` · ${s.bars} bars` : ""}`); }}
                          className={`flex-1 h-7 rounded-md flex items-center justify-center text-[11px] font-medium px-1 truncate transition-all ${on ? "ring-2 ring-gold scale-[1.03]" : "hover:brightness-125"}`}
                          style={{ background: toneBg[s.tone], color: toneText[s.tone] }}>{s.name}</button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="relative flex-1 mt-3">
                {/* playhead (position set imperatively via ref to survive JSX transform) */}
                {showPlayhead && (
                  <div ref={phRef} data-testid="timeline-playhead" className="absolute top-0 bottom-8 z-10 pointer-events-none">
                    <div className="w-px h-full" style={{ background: "var(--gold)" }} />
                    <div className="w-0 h-0 -ml-1.5" style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "7px solid var(--gold)" }} />
                  </div>
                )}
                <div className="space-y-2">
                  {TRACKS.map((t) => {
                    const muted = laneMuted(t.name);
                    const isMuted = mutes.has(t.name);
                    const isSolo = solos.has(t.name);
                    return (
                    <div key={t.name} data-testid={`track-row-${t.name.replace(/\s*\/\s*/g, "-").replace(/\s+/g, "-").toLowerCase()}`} className="flex gap-3 items-stretch min-w-0" style={{ height: 40 }}>
                      <div className="w-[110px] sm:w-[132px] shrink-0 flex items-center gap-2.5">
                        <t.icon className="w-4 h-4" style={{ color: t.color === "#9aa0ab" ? "#8a8f9c" : t.color }} />
                        <div className="leading-tight min-w-0">
                          <p className="text-slate-200 text-[13px] truncate">{t.name}</p>
                          <p className="font-mono-a text-[10px] text-slate-600 flex items-center gap-1">
                            {t.sub && <span className="mr-0.5">{t.sub}</span>}
                            <button data-testid={`mute-${t.name.replace(/\s*\/\s*/g, "-").replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleMute(t.name)}
                              className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${isMuted ? "bg-amber-500/20 text-amber-400" : "hover:text-slate-300"}`} title={isMuted ? "Unmute" : "Mute"}>M</button>
                            <button data-testid={`solo-${t.name.replace(/\s*\/\s*/g, "-").replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleSolo(t.name)}
                              className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${isSolo ? "bg-emerald-500/20 text-emerald-400" : "hover:text-slate-300"}`} title={isSolo ? "Unsolo" : "Solo"}>S</button>
                            <span className="w-1.5 h-1.5 rounded-full ml-0.5" style={{ background: t.color }} />
                          </p>
                        </div>
                      </div>
                      <div className={`flex-1 min-w-0 rounded-md flex gap-1 p-0.5 overflow-hidden transition-opacity ${muted ? "opacity-25" : "opacity-100"}`} style={{ background: "#0e0e11", border: "1px solid #1c1c22", color: t.color }}>
                        {t.type === "wave" ? <Wave color={t.color} />
                          : t.type === "fx" ? (
                            <div className="w-full h-full rounded" style={{ background: `linear-gradient(90deg, ${t.color}00, ${t.color}40, ${t.color}12)` }} />
                          ) : (<><Clip color={t.color} span={5} /><Clip color={t.color} span={5} /></>)}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* verified action */}
              <button data-testid="verified-action-bar" onClick={done ? scrollToDeliver : focusAdjust}
                className="mt-4 w-full text-left rounded-xl border flex items-center justify-between px-4 py-3 transition-colors hover:brightness-125" style={{ background: "#14110a", borderColor: "#3a3320" }}>
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-gold" />
                  <span className="text-slate-200 text-sm">{done ? "Verified action: build complete · package written" : running ? (events[events.length-1]?.message || "Working…") : "Ready, review DNA then build your session"}</span>
                </div>
                <span className="font-mono-a text-xs text-slate-500 flex items-center gap-2">{running ? "Live" : done ? "Done" : "Idle"} <ChevronRight className="w-4 h-4" /></span>
              </button>
            </div>

            {/* AURA <-> DAW live conversation */}
            <div className="border-t px-5 py-4" style={{ borderColor: "var(--daw-border)" }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-slate-100 font-medium text-sm flex items-center gap-2"><AudioLines className="w-4 h-4 text-gold" /> Engine ⇄ {daw.toUpperCase()}</p>
                {running ? <span className="text-xs text-emerald-400 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live</span> : <span className="text-xs text-slate-600">{convo.length ? "Session log" : "Idle"}</span>}
              </div>
              {approach && (
                <div className="mb-3 rounded-xl border px-4 py-3" style={{ background: "#14110a", borderColor: "#3a3320" }}>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-gold mb-1 font-mono-a">Engine approach</p>
                  <p className="text-slate-300 text-sm leading-relaxed">{approach}</p>
                </div>
              )}
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="conversation-feed">
                <AnimatePresence>
                  {convo.map((m) => {
                    const who = m.speaker;
                    const mine = who === "user";
                    const label = who === "aura" ? "AURA" : who === "daw" ? daw.toUpperCase() : "YOU";
                    const color = who === "aura" ? "#C8A15C" : who === "daw" ? "#4fbf9f" : "#9aa0ab";
                    return (
                      <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${who}`}>
                        <div className="max-w-[88%] rounded-xl px-3 py-2" style={mine ? { background: "rgba(255,255,255,0.06)" } : { background: `${color}14`, border: `1px solid ${color}33` }}>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] font-mono-a tracking-wider" style={{ color }}>{label}</span>
                            {m.target && <span className="text-[10px] text-slate-500">→ {m.target}</span>}
                          </div>
                          <p className="text-slate-200 text-sm leading-snug">{m.message}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                {!convo.length && <p className="text-slate-600 text-sm">No session yet. Hit <span className="text-gold">Build in {daw.toUpperCase()}</span> and I'll drive your DAW, loading your packs and narrating every move right here.</p>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input ref={adjustRef} data-testid="adjust-input" value={adjustText} onChange={(e) => setAdjustText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendAdjust(); }}
                  placeholder={convo.length ? "Tell the engine what to change… e.g. 'darker chords, harder 808'" : "Build first, then adjust live"}
                  disabled={adjusting || !convo.length}
                  className="flex-1 bg-black/30 border rounded-lg px-3 h-10 text-sm text-slate-200 outline-none placeholder:text-slate-600 disabled:opacity-50" style={{ borderColor: "var(--daw-border)" }} />
                <button data-testid="adjust-send-btn" onClick={sendAdjust} disabled={adjusting || !adjustText.trim()} className="h-10 px-4 rounded-lg btn-gold text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5">{adjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Adjust</button>
              </div>
              {done && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid="download-package-btn" onClick={() => blobDownload(`/artifacts/${job.artifact_id}/download`, `${project.name}.zip`)} className="rounded-lg btn-gold text-sm font-semibold px-4 py-2 flex items-center gap-1.5"><Download className="w-4 h-4" /> Package</button>
                  <button data-testid="export-stems-btn" onClick={() => exportBounce("stem")} disabled={exporting} className="rounded-lg bg-white/[0.06] hover:bg-white/10 text-slate-200 text-sm px-4 py-2 flex items-center gap-1.5">{exporting==="stem"?<Loader2 className="w-4 h-4 animate-spin"/>:<SlidersHorizontal className="w-4 h-4 text-violet" />} Stems</button>
                  <button data-testid="export-final-btn" onClick={() => exportBounce("final")} disabled={exporting} className="rounded-lg bg-white/[0.06] hover:bg-white/10 text-slate-200 text-sm px-4 py-2 flex items-center gap-1.5">{exporting==="final"?<Loader2 className="w-4 h-4 animate-spin"/>:<Disc3 className="w-4 h-4 text-amber-400" />} Session</button>
                  {devices.map((d) => (
                    <button key={d.id} data-testid={`push-device-${d.id}`} onClick={() => pushToDevice(d.id)} className="rounded-lg bg-white/[0.06] hover:bg-white/10 text-slate-200 text-sm px-4 py-2 flex items-center gap-1.5"><Send className="w-4 h-4 text-emerald-400" /> {d.daw.toUpperCase()}</button>
                  ))}
                </div>
              )}
              {done && <MatchedSounds pid={pid} />}
            </div>
          </div>
        </div>

        {/* Transport bar */}
        <div className="min-h-[72px] shrink-0 border-t flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-2" style={{ borderColor: "var(--daw-border)", background: "#0C0C0F" }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--daw-border)" }}>
              <Music2 className="w-4 h-4 text-gold" />
              <div className="leading-tight min-w-0"><p className="text-slate-200 text-sm truncate max-w-[120px]">{project?.name || "Session"}</p><p className="font-mono-a text-[10px] text-slate-600">v·e</p></div>
              <ChevronDown className="w-4 h-4 text-slate-600" />
            </div>
            <span className="font-mono-a text-xs text-slate-500 hidden sm:inline">{bpm} BPM · 4/4 · {totalBars} bars</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button data-testid="build-btn" onClick={build} disabled={running}
              className="h-11 px-4 sm:px-6 rounded-lg btn-gold font-semibold flex items-center gap-2 disabled:opacity-60">
              <AuraMark className="w-4 h-4" /> <span className="whitespace-nowrap">{running ? "Building…" : `Build`}<span className="hidden sm:inline">{running ? "" : ` in ${daw.toUpperCase()}`}</span></span>
            </button>
            <button data-testid="pause-btn" onClick={() => toast.info("Paused")} className="h-11 px-4 sm:px-5 rounded-lg border text-slate-200 flex items-center gap-2" style={{ borderColor: "var(--daw-border)", background: "#141418" }}>
              <Pause className="w-4 h-4" /> <span className="hidden sm:inline">Pause</span>
            </button>
            <button data-testid="emergency-stop-btn" onClick={stop} className="h-11 px-4 sm:px-5 rounded-lg border flex items-center gap-2" style={{ borderColor: "#5a2222", color: "#f0797a", background: "#1a0f0f" }}>
              <Square className="w-4 h-4" /> <span className="hidden sm:inline">Emergency Stop</span>
            </button>
          </div>
          <div className="text-right hidden lg:block">
            <p className="font-display text-slate-300 text-sm">AuraSession</p>
            <p className="font-mono-a text-[10px] tracking-[0.2em] text-slate-600 uppercase">Create / Produce / Release</p>
          </div>
        </div>
      </div>
    </div>
  );
}
